import React from "react";
import {
  CheckCircle2,
  FileText,
  Home,
  Handshake,
  ArrowRight,
  X,
  Banknote,
  UsersRound,
  Lightbulb,
  FileCheck2,
  LockKeyhole,
  Landmark,
  MessagesSquare,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./FirstLoginInfoModal.css";

export default function HomebuyerFirstLoginModal({
  open,
  isClosing = false,
  onClose,
  onStartOss,
  dossierOrigin = "",
  hasOssConnection = false,
  hasAgreedLoanTerms = false,
}) {
  const { t } = useTranslation("homebuyer-first-login");

  if (!open) return null;

  const normalizedDossierOrigin = String(dossierOrigin || "").toUpperCase();
  const isLenderCreatedDossier = normalizedDossierOrigin === "LENDER_MANUAL";
  const canStartOss = Boolean(onStartOss) && !hasOssConnection;

  const handleClose = () => {
    if (isClosing) return;
    onClose?.();
  };

  const quickBenefits = [
    { icon: FileText, key: "structuredDossier" },
    { icon: Handshake, key: "coordinatedSupport" },
    { icon: Lightbulb, key: "betterChoices" },
  ];

  const supportAreas = [
    { icon: Home, key: "technical" },
    { icon: Banknote, key: "financial" },
    { icon: FileCheck2, key: "administrative" },
  ];

  const advantages = [
    "advantage1",
    "advantage2",
    "advantage3",
    "advantage4",
    "advantage5",
  ];

  return (
    <div
      className={`hb-first-login-backdrop ${
        isClosing ? "hb-first-login-backdrop--closing" : ""
      }`}
      onClick={handleClose}
    >
      <section
        className={`hb-first-login ${
          isClosing ? "hb-first-login--closing" : ""
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hb-first-login-title"
      >
        <button
          type="button"
          className="hb-first-login__close"
          onClick={handleClose}
          aria-label={t("buttons.close", "Close")}
        >
          <X size={18} />
        </button>

        <div className="hb-first-login__top-pills">
          <span>{t("pills.dossier", "Dossier")}</span>
          <span>{t("pills.financing", "Financing")}</span>
          <span>{t("pills.oss", "One-Stop Shop")}</span>
        </div>

        <div className="hb-first-login__hero">
          <div className="hb-first-login__hero-main">
            <div className="hb-first-login__mini-title">
              <CheckCircle2 size={16} />
              <span>{t("eyebrow", "Your LEG-UP workspace is ready")}</span>
            </div>

            <h2 id="hb-first-login-title">
              {t("title", "Your homebuyer journey in one place")}
            </h2>

            <p>
              {isLenderCreatedDossier
                ? t(
                    "introLenderCreated",
                    "Your lender has already created and linked your dossier to your account. You can now review your information, follow the financing process, communicate with the relevant actors and continue your renovation journey through LEG-UP.",
                  )
                : t(
                    "intro",
                    "LEG-UP brings your dossier, financing process, communication and renovation support together in one structured workspace.",
                  )}
            </p>
          </div>

          <aside className="hb-first-login__why">
            <strong>{t("why.title", "Why this matters")}</strong>
            <p>
              {t(
                "why.text",
                "Each actor has a clear role, while you keep visibility over the progress of your case and the next steps that require your attention.",
              )}
            </p>
          </aside>
        </div>

        <div className="hb-first-login__quick-benefits">
          {quickBenefits.map(({ icon: Icon, key }) => (
            <article key={key} className="hb-first-login__quick-card">
              <div className="hb-first-login__icon">
                <Icon size={17} />
              </div>
              <strong>{t(`quickBenefits.${key}.title`)}</strong>
              <p>{t(`quickBenefits.${key}.text`)}</p>
            </article>
          ))}
        </div>

        <div className="hb-first-login__section-heading">
          {t("journeyTitle", "How the workflow works")}
        </div>

        <div className="hb-first-login__journey">
          <article>
            <div className="hb-first-login__journey-icon">
              <FileText size={19} />
            </div>
            <div className="hb-first-login__journey-content">
              <span className="hb-first-login__step-label">
                {t("dossier.step", "Step 1")}
              </span>
              <strong>
                {isLenderCreatedDossier
                  ? t(
                      "dossier.lenderCreatedTitle",
                      "Your dossier is already linked",
                    )
                  : t("dossier.title", "Build and complete your dossier")}
              </strong>
              <p>
                {isLenderCreatedDossier
                  ? t(
                      "dossier.lenderCreatedText",
                      "Your lender created the case and linked the dossier to your account. Review the available information and complete any steps that still require your input.",
                    )
                  : t(
                      "dossier.text",
                      "Your personal, financial, property and supporting information is organised in one dossier that can be completed progressively.",
                    )}
              </p>
            </div>
          </article>

          <article>
            <div className="hb-first-login__journey-icon">
              <Landmark size={19} />
            </div>
            <div className="hb-first-login__journey-content">
              <span className="hb-first-login__step-label">
                {t("lender.step", "Step 2")}
              </span>
              <strong>
                {t("lender.title", "Financing and lender decision")}
              </strong>
              <p>
                {t(
                  "lender.text",
                  "Your lender assesses the financing case and, after the relevant discussion with you, records the agreed loan terms and final loan decision in LEG-UP.",
                )}
              </p>
            </div>
          </article>

          <article className="hb-first-login__journey-card--highlight">
            <div className="hb-first-login__journey-icon">
              <Handshake size={19} />
            </div>
            <div className="hb-first-login__journey-content">
              <span className="hb-first-login__step-label">
                {t("oss.step", "Step 3")}
              </span>
              <strong>
                {hasOssConnection
                  ? t("oss.connectedTitle", "Your One-Stop Shop is connected")
                  : t("oss.title", "Connect with a One-Stop Shop")}
              </strong>
              <p>
                {hasOssConnection
                  ? t(
                      "oss.connectedText",
                      "Your OSS relationship is already active. You can use it to coordinate technical, administrative and renovation-related activities.",
                    )
                  : t(
                      "oss.text",
                      "Choose an OSS to support the renovation journey, including technical guidance, planning, administrative coordination and available subsidy opportunities.",
                    )}
              </p>
            </div>
          </article>
        </div>

        <div className="hb-first-login__workflow-note-grid">
          <article
            className={`hb-first-login__workflow-note ${
              hasAgreedLoanTerms ? "hb-first-login__workflow-note--active" : ""
            }`}
          >
            <div className="hb-first-login__workflow-note-icon">
              <LockKeyhole size={18} />
            </div>
            <div>
              <strong>
                {hasAgreedLoanTerms
                  ? t("locking.activeTitle", "Your core dossier is now locked")
                  : t("locking.title", "Dossier locking after agreement")}
              </strong>
              <p>
                {hasAgreedLoanTerms
                  ? t(
                      "locking.activeText",
                      "The agreed financing terms have been recorded, so the core dossier can no longer be altered. Communication, renovation activities and the later project workflow continue normally.",
                    )
                  : t(
                      "locking.text",
                      "Once the lender records the agreed loan terms and approved decision, the core dossier becomes read-only so the agreed case remains consistent.",
                    )}
              </p>
            </div>
          </article>

          <article className="hb-first-login__workflow-note">
            <div className="hb-first-login__workflow-note-icon">
              <MessagesSquare size={18} />
            </div>
            <div>
              <strong>
                {t(
                  "communication.title",
                  "Communication is created automatically",
                )}
              </strong>
              <p>
                {t(
                  "communication.text",
                  "You do not need to create communication threads manually. Relevant conversations become available automatically when the corresponding lender or OSS relationship is active.",
                )}
              </p>
            </div>
          </article>
        </div>

        <div className="hb-first-login__section-heading">
          {t("supportAreasTitle", "What your One-Stop Shop can support")}
        </div>

        <div className="hb-first-login__support-grid">
          {supportAreas.map(({ icon: Icon, key }) => (
            <article key={key} className="hb-first-login__support-card">
              <div className="hb-first-login__support-icon">
                <Icon size={18} />
              </div>
              <div>
                <strong>{t(`supportAreas.${key}.title`)}</strong>
                <p>{t(`supportAreas.${key}.text`)}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="hb-first-login__lower-grid">
          <article className="hb-first-login__control-card">
            <strong>{t("control.title", "You remain in control")}</strong>
            <p>
              {t(
                "control.text",
                "LEG-UP coordinates the process, but your lender and OSS remain responsible for their respective activities. You can follow progress, review information and communicate with them from the same workspace.",
              )}
            </p>
          </article>

          <article className="hb-first-login__advantages-card">
            <strong>{t("advantages.title", "What you gain")}</strong>
            <div className="hb-first-login__advantages-list">
              {advantages.map((key, index) => (
                <div key={key} className="hb-first-login__advantage">
                  <span>{index + 1}</span>
                  <p>{t(`advantages.${key}`)}</p>
                </div>
              ))}
            </div>
          </article>
        </div>

        <div className="hb-first-login__footer-note">
          <UsersRound size={17} />
          <p>
            {t(
              "footerNote",
              "Your lender, One-Stop Shop and other relevant actors only see and manage the information required for their role in the process.",
            )}
          </p>
        </div>

        <div className="hb-first-login__actions">
          <button
            type="button"
            className="hb-first-login__btn hb-first-login__btn--ghost"
            onClick={handleClose}
          >
            {t("buttons.continue", "Continue to dashboard")}
          </button>

          {canStartOss ? (
            <button
              type="button"
              className="hb-first-login__btn hb-first-login__btn--primary"
              onClick={() => {
                handleClose();
                window.setTimeout(() => {
                  onStartOss?.();
                }, 420);
              }}
            >
              {t("buttons.connectOss", "Connect with an OSS")}
              <ArrowRight size={16} />
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
